import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl'

import MuiAppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Button from '@material-ui/core/Button';
import Hidden from '@material-ui/core/Hidden';
import Box from '@material-ui/core/Box';

import { Theme } from '../Theme';
import { ThemeConfig } from '../themes';
import SocialMediaGrid from './SocialMediaGrid';
import NavLogo from './NavLogo';
import NavDrawer, { NavDrawerProvider, NavDrawerClasses } from './NavDrawer';
import NavLinks from './NavLinks';
import { usePreviewConfig, useFeatureEnables } from '../dataProviders';
import { CONTENT_WIDTH, MOBILE_CONTENT_WIDTH } from './defaultStyleVars';
import { makeOverridableStyles, StyleDef } from '../utils';
import { ReactComponent as MenuSvg } from './Menu.svg';

export { makeNavDrawerStyles } from './NavDrawer';


interface NavStyles {
  root?: StyleDef;
  toolbar?: StyleDef;
  paper?: StyleDef;
  menuLink?: StyleDef;
  link?: StyleDef;
  socialMediaGrid?: StyleDef;
  socialMediaLink?: StyleDef;
  socialMediaLinkIcon?: StyleDef;
}
export interface NavClasses {
  root?: string,
  toolbar?: string,
  paper?: string,
  menuLink?: string,
  link?: string,
  socialMediaGrid?: string,
  socialMediaLink?: string,
  socialMediaLinkIcon?: string;
}

interface NavLogoStyles {
  textLogo?: StyleDef;
  root?: StyleDef;
  image?: StyleDef;
}
interface NavLogoClasses {
  textLogo?: string;
  root?: string;
  image?: string;
}

export default function SharedAppBar({ navClasses={}, navDrawerClasses, logoClasses }:{
  navClasses: NavClasses,
  navDrawerClasses: NavDrawerClasses,
  logoClasses: NavLogoClasses
}) {
  const intl = useIntl();
  const { showMenuLinks, showSocialMedia } = useFeatureEnables();

  const { mobileNavOpen } = usePreviewConfig();
  const [drawerState, setDrawerState] = useState(false);
  const toggleDrawer = (isOpen: boolean) => (event: any) => { // TODO: Find the proper typing for this event
    if (event && event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerState(isOpen);
  };
  const openMobileNavAriaLabel = intl.formatMessage({ description: "openMobileNavAriaLabel", defaultMessage: "Open Navigation" });

  useEffect( () => {
    if( mobileNavOpen === true || mobileNavOpen === false ) {
      setDrawerState(mobileNavOpen);
    }
  }, [mobileNavOpen] );

  return (
    <NavDrawerProvider>
      <MuiAppBar position="static" classes={{ root: navClasses.root }}>
        <Toolbar classes={{ root: navClasses.toolbar }}>
          <Hidden smDown>
            <Box flexGrow={1}>
              <NavLogo classes={logoClasses} />
              <span aria-label="Main Navigation">
                <NavLinks linkClassName={navClasses.link} />
              </span>
            </Box>
            <SocialMediaGrid
              classes={{
                root: navClasses.socialMediaGrid,
                link: navClasses.socialMediaLink,
                linkIcon: navClasses.socialMediaLinkIcon
              }}
            />
          </Hidden>
          <Hidden mdUp>
            { (showMenuLinks || showSocialMedia) &&
              <Button onClick={toggleDrawer(true)} className={navClasses.menuLink} aria-label={openMobileNavAriaLabel}>
                <MenuSvg />
              </Button>
            }
            <NavLogo classes={logoClasses} />
            <NavDrawer toggleDrawer={toggleDrawer} open={drawerState} classes={navDrawerClasses} logoClasses={logoClasses} navClasses={navClasses} />
          </Hidden>
        </Toolbar>
      </MuiAppBar>
    </NavDrawerProvider>
  );
}


export function makeLogoStyles(styleOverrides: NavLogoStyles | ((theme:ThemeConfig)=>NavLogoStyles)) {
  return makeOverridableStyles(
    (theme: Theme) => ({
      textLogo: {
        textAlign: 'left',
        display: 'inline-block',
        marginLeft: 0,
        fontSize: 20,
        fontWeight: '500',
        lineHeight: '24px',
        color: '#222222',
        [theme.breakpoints.down('sm')]: {
          textAlign: 'left'
        },
        [theme.breakpoints.up('xs')]: {
          textAlign: 'center'
        },
      },
      root: {
        textAlign: 'left',
        display: 'inline-block',
        verticalAlign: 'middle',
        marginLeft: 0,
        marginRight: '5px',
        [theme.breakpoints.down('md')]: {
          margin: '0 auto'
        },
      },
      image: {
        maxHeight: '45px',
        maxWidth: '150px'
      }
    }),
    styleOverrides,
    { name: 'LogoStyles' }
  );
}

export function makeNavStyles(styleOverrides: NavStyles | ((theme:ThemeConfig)=>NavStyles)) {
  return makeOverridableStyles(
    (theme: Theme) => ({
      root: {
        boxShadow: 'none',
        backgroundColor: 'white',
        border: '1px solid rgba(0, 0, 0, 0.12)',
      },
      toolbar: () => ({
        maxHeight: '64px',
        backgroundColor: 'white',
        width: MOBILE_CONTENT_WIDTH,
        padding: 0,
        margin: '0 auto',
        flexWrap: 'wrap',
        [theme.breakpoints.up('md')]: {
          width: `${CONTENT_WIDTH}px`,
          maxWidth: '98%'
        }
      }),
      paper: {},
      menuLink: {
        position: 'absolute',
        marginLeft: '-19px',
        '& svg': {
          transform: 'scale(1.6)',
          fill: theme.link.color
        }
      },
      link: {
        fontSize: '14px',
        verticalAlign: 'middle',
        marginLeft: '15px',
      },
      socialMediaGrid: {
        display: 'flex',
      },
      socialMediaLink: {
        padding: '0 5px',
        fontSize: '18px',
        marginLeft: 'auto',
        '&:last-child': {
          paddingRight: '0'
        },
      },
      socialMediaLinkIcon: {
        verticalAlign: 'middle',
      }
    }),
    styleOverrides,
    { name: 'NavStyles' }
  );
};


